/**
 * @brisa/ui · Banner
 *
 * Banner persistente para feedback inline (info/warning/success/error).
 * Usado historicamente em rotas auth ("Confirme seu email"), e agora
 * estendido em W3.2 (admin-basico-login) com:
 *   - visual facelift v1 (Tailwind + tokens) substituindo CSS classes legacy
 *   - tone `info` com bg amarelo soft + border amarelo + ícone Info default
 *   - variant `error` (alias visual de warning destructive)
 *   - prop `icon` opcional para sobrescrever o ícone default por variant
 *
 * API mantida 100% retrocompatível com a versão CSS-legacy: todos os usos
 * existentes (`variant="info|warning|success"`, `message`, `cta`,
 * `dismissible`, `onDismiss`) continuam funcionando.
 *
 * Sem 'use client' — apresentacional puro; CTA + dismiss usam callbacks
 * que o caller provê (Client Component decide a boundary).
 *
 * UI_SPEC §2.4 + §5 · COMPONENT_MAP §4 (STORY PLAN Banner.info)
 */
import * as React from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';

import { cn } from './lib/utils';

export type BannerVariant = 'warning' | 'info' | 'success' | 'error';

export interface BannerProps {
  variant?: BannerVariant;
  message: React.ReactNode;
  cta?: { label: string; onClick: () => void };
  dismissible?: boolean;
  onDismiss?: () => void;
  /** Sobrescreve o ícone default do variant. Passe `null` para ocultar. */
  icon?: React.ReactNode | null;
  className?: string;
}

const VARIANT_CLASSES: Record<BannerVariant, string> = {
  // Bg amarelo soft + border amarelo — UI_SPEC §2.4 tone info
  info: 'bg-[hsl(var(--accent-yellow)/0.12)] border-[hsl(var(--accent-yellow)/0.4)]',
  // Bg destructive soft + border destructive — UI_SPEC §2.4 banner role-required+mismatch
  warning: 'bg-[hsl(var(--destructive)/0.08)] border-[hsl(var(--destructive)/0.4)]',
  // Bg destructive mais saturado — credenciais inválidas (UI_SPEC §2.4)
  error: 'bg-[hsl(var(--destructive)/0.12)] border-[hsl(var(--destructive)/0.6)]',
  // Bg verde soft + border verde — feedback positivo claro (token --success)
  success: 'bg-[hsl(var(--success)/0.12)] border-[hsl(var(--success)/0.5)]',
};

const ICON_CLASSES: Record<BannerVariant, string> = {
  info: 'text-[hsl(var(--text-100))]',
  warning: 'text-[hsl(var(--destructive))]',
  error: 'text-[hsl(var(--destructive))]',
  success: 'text-[hsl(var(--success))]',
};

const DEFAULT_ICONS: Record<BannerVariant, React.ReactNode> = {
  info: <Info size={16} aria-hidden="true" />,
  warning: <AlertTriangle size={16} aria-hidden="true" />,
  error: <XCircle size={16} aria-hidden="true" />,
  success: <CheckCircle2 size={16} aria-hidden="true" />,
};

const ROLE_BY_VARIANT: Record<BannerVariant, 'status' | 'alert'> = {
  info: 'status',
  warning: 'status',
  success: 'status',
  error: 'alert',
};

const ARIA_LIVE_BY_VARIANT: Record<BannerVariant, 'polite' | 'assertive'> = {
  info: 'polite',
  warning: 'polite',
  success: 'polite',
  error: 'assertive',
};

export function Banner({
  variant = 'info',
  message,
  cta,
  dismissible = false,
  onDismiss,
  icon,
  className,
}: BannerProps) {
  const resolvedIcon = icon === undefined ? DEFAULT_ICONS[variant] : icon;
  return (
    <div
      role={ROLE_BY_VARIANT[variant]}
      aria-live={ARIA_LIVE_BY_VARIANT[variant]}
      data-slot="banner"
      data-variant={variant}
      className={cn(
        'flex w-full items-start gap-3 rounded-sm border px-4 py-3.5 font-body text-sm leading-snug',
        'text-[hsl(var(--text-100))]',
        VARIANT_CLASSES[variant],
        className,
      )}
    >
      {resolvedIcon !== null && (
        <span
          data-slot="banner-icon"
          className={cn('mt-0.5 inline-flex flex-shrink-0', ICON_CLASSES[variant])}
        >
          {resolvedIcon}
        </span>
      )}
      {/* Wrapper interno: mobile = flex-col (CTA na linha de baixo, alinhado
          à esquerda com a mensagem); md+ = flex-row (CTA à direita do texto).
          Evita o cenário cramped do screenshot ("Sua sessão expirou ·
          Tentar novamente" em colunas apertadas). */}
      <div
        data-slot="banner-body"
        className="flex flex-1 flex-col items-start gap-2 md:flex-row md:items-center md:gap-3"
      >
        <span data-slot="banner-message" className="md:flex-1">
          {message}
        </span>
        {cta && (
          <button
            type="button"
            onClick={cta.onClick}
            data-slot="banner-cta"
            className={cn(
              'flex-shrink-0 font-semibold underline underline-offset-2',
              'text-[hsl(var(--text-100))] hover:text-[hsl(var(--text-80))]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            )}
          >
            {cta.label}
          </button>
        )}
      </div>
      {dismissible && onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Fechar"
          data-slot="banner-dismiss"
          className={cn(
            'flex-shrink-0 text-[hsl(var(--text-40))] hover:text-[hsl(var(--text-100))]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          )}
        >
          {/* Mantém glifo simples — caller pode passar wrapper se quiser ícone lucide */}
          <span aria-hidden="true">✕</span>
        </button>
      )}
    </div>
  );
}
